import { Check } from "lucide-react";
import { formatPrice } from "@/lib/currency";

const TIERS = [
  {
    name: "Starter",
    gbp: 150,
    blurb: "A clean one-pager that puts you on the map. Perfect for a side project or a tradesperson.",
    features: ["1 page, mobile-first", "Contact email / WhatsApp link", "Basic SEO setup", "Live within 7 days"],
    testid: "tier-starter",
  },
  {
    name: "Standard",
    gbp: 300,
    blurb: "A small site that does real work — about, services, contact form, the lot.",
    features: ["Up to 4 pages", "Contact form (saved + emailed)", "Custom design, your brand", "Light copy polishing", "Live within 7 days"],
    testid: "tier-standard",
    highlight: true,
  },
  {
    name: "Premium",
    gbp: 500,
    blurb: "A proper multi-page site with the works — perfect when you need to look serious.",
    features: ["Up to 8 pages", "Blog / news section", "Custom animations + design system", "Detailed SEO + analytics", "Live within 7 days"],
    testid: "tier-premium",
  },
];

export default function Tiers({ currency, rates }) {
  return (
    <section id="pricing" data-testid="pricing-tiers" className="border-t border-[#E5E0D8]">
      <div className="oak-container oak-section">
        <div className="grid lg:grid-cols-[1fr_1.6fr] gap-12 lg:gap-20 items-start">
          <div className="lg:sticky lg:top-28">
            <p className="text-sm uppercase tracking-[0.18em] text-[#5C665F]">Packages</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight mt-3 text-[#1A3622]">
              Three starting points. <br />Build only what you need.
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-[#5C665F]">
              Each tier is a fixed-price starting point. Use the calculator below to add only the
              pieces that matter for your business.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TIERS.map((t) => (
              <article
                key={t.name}
                data-testid={t.testid}
                className={[
                  "relative rounded-2xl p-6 md:p-7 transition-all hover:-translate-y-1",
                  t.highlight
                    ? "bg-[#1A3622] text-white border border-[#1A3622] shadow-lg"
                    : "bg-white text-[#1C211E] border border-[#E5E0D8] hover:shadow-md",
                ].join(" ")}
              >
                {t.highlight && (
                  <span
                    data-testid="tier-popular-badge"
                    className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-[#C17F59] text-white text-[11px] uppercase tracking-[0.15em] px-3 py-1"
                  >
                    Most chosen
                  </span>
                )}
                <h3
                  className={
                    "font-heading font-bold text-2xl " +
                    (t.highlight ? "text-white" : "text-[#1A3622]")
                  }
                >
                  {t.name}
                </h3>
                <p className={"mt-2 text-sm " + (t.highlight ? "text-white/70" : "text-[#5C665F]")}>
                  from
                </p>
                <p
                  data-testid={`${t.testid}-price`}
                  className={
                    "font-heading font-extrabold tracking-tight text-4xl md:text-5xl mt-1 " +
                    (t.highlight ? "text-white" : "text-[#1A3622]")
                  }
                >
                  {formatPrice(t.gbp, currency, rates)}
                </p>
                <p className={"mt-4 text-[15px] " + (t.highlight ? "text-white/80" : "text-[#5C665F]")}>
                  {t.blurb}
                </p>
                <ul className="mt-6 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[15px]">
                      <Check
                        className={
                          "w-4 h-4 mt-1 shrink-0 " +
                          (t.highlight ? "text-[#C17F59]" : "text-[#2B5C40]")
                        }
                      />
                      <span className={t.highlight ? "text-white/90" : "text-[#1C211E]"}>{f}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
